/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { InputMask } from '@react-input/mask';
import { useMutation, useQuery } from 'react-query';
import { isIOS } from 'react-device-detect';
import {
  OnboardingBodyLayout,
  SelectCountryDropdown,
  CustomDropdown,
  MAIN_ICON_COLORS,
} from 'assets/components';
import Input from '../../../components/Input';
import { handleFormBlur } from '../../../utilities/formsUtils';
import { validateLatinLettersOnly, validateSSN } from '../../../utilities/formValidation';
import { useHistoryState } from '../../../utilities/sharedHooks/history';
import { PARTIAL_SIGN_UP_PHASES, US_COUNTRY_ALPHA } from '../constants';
import { PARTIAL_SIGN_UP } from '../mutations';
import { showAlert } from '../../../utilities/notifications';
import {
  SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY,
  SETTINGS_TIER_STANDARD_ALL_KEY,
} from '../../../utilities/sharedQueries';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import { FLOWS, sendStats } from '../../../services/mixpanel';
import {
  MXP_ONBOARDING_SIGN_UP_PERSONAL_INFO_VISIT,
  MXP_ONBOARDING_SIGN_UP_PERSONAL_INFO_CONFIRM,
} from '../mixpanel-events';
import Icon from '../../../components/Icon';
import { ICONS } from '../../../components/Icon/constants';
import { useSetting } from '../../../utilities/sharedHooks/settings';
import { EUROPE_COUNTRIES_ALPHA3 } from '../../../utilities/constants';
import { isConnectFinancialCustodyCountry } from '../../../utilities/custodyProvider';

const AddPersonalInformation = () => {
  const { t: translate } = useTranslation();

  const { state } = useLocation();
  const history = useHistory();

  const { state: onboardingState } = useContext(OnboardingContext);
  const allowPartialOnboarding = onboardingState?.mainSettings?.feature_enable_partial_registration;

  const [isPurposeOfAccountOpened, setIsPurposeOfAccountOpened] = useState(false);
  const [displayBirthDateLabel, setDisplayBirtDateLabel] = useState(false);

  const [isBOPPhaseOn] = useSetting('server_configurations', 'feature_toggle_bop_onboarding');

  const [form, setForm] = useHistoryState('form', {
    first_name: '',
    middle_name: '',
    last_name: '',
    dateOfBirth: '',
    place_of_birth_id: '',
    place_of_birth_label: '',
    place_of_birth_iso3166alpha3: '',
    place_of_birth_iso3166alpha2: '',
    selected_country_of_nationality_id: state?.countryOfResidenceId || '',
    selected_country_of_nationality_label: state?.countryOfResidenceLabel || '',
    selected_country_of_nationality_iso3166alpha3: state?.countryOfResidenceAlpha || '',
    selected_country_of_nationality_iso3166alpha2: state?.countryOfResidenceAlpha2 || '',
    // second nationality
    second_selected_country_of_nationality_id: '',
    second_selected_country_of_nationality_label: '',
    second_selected_country_of_nationality_iso3166alpha3: '',
    second_selected_country_of_nationality_iso3166alpha2: '',
    // third nationality
    third_selected_country_of_nationality_id: '',
    third_selected_country_of_nationality_label: '',
    third_selected_country_of_nationality_iso3166alpha3: '',
    third_selected_country_of_nationality_iso3166alpha2: '',
    ssn: '',
    purposeOfAccount: {
      description: '',
      key: '',
    },
    portugalInvestmentScheme: false,
  });

  const isCorEuOrUk = useMemo(
    () => [...EUROPE_COUNTRIES_ALPHA3, 'GBR'].includes(state?.countryOfResidenceAlpha),
    [state?.countryOfResidenceAlpha]
  );

  const { data: supportedCountries } = useQuery(SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY);
  const shouldAskAdditionalDetails = useMemo(
    () =>
      (isBOPPhaseOn && isCorEuOrUk) ||
      isConnectFinancialCustodyCountry(supportedCountries, state?.countryOfResidenceAlpha),
    [isBOPPhaseOn, isCorEuOrUk, supportedCountries, state?.countryOfResidenceAlpha]
  );

  const isCountryOfBirthAsked = useMemo(
    () =>
      isBOPPhaseOn &&
      (isCorEuOrUk ||
        (state?.countryOfResidenceAlpha !== US_COUNTRY_ALPHA &&
          isConnectFinancialCustodyCountry(supportedCountries, state?.countryOfResidenceAlpha))),
    [isBOPPhaseOn, isCorEuOrUk, supportedCountries, state?.countryOfResidenceAlpha]
  );

  const isCORPortugal = useMemo(
    () => state?.countryOfResidenceAlpha2 === 'PT',
    [state?.countryOfResidenceAlpha2]
  );

  const [additionalNationalities, setAdditionalNationalities] = useState(
    state?.additionalNationalities || 0
  );

  const isUS =
    state?.countryOfResidenceAlpha === US_COUNTRY_ALPHA ||
    form?.selected_country_of_nationality_iso3166alpha3 === US_COUNTRY_ALPHA;

  useEffect(() => {
    sendStats(MXP_ONBOARDING_SIGN_UP_PERSONAL_INFO_VISIT, {}, FLOWS.ONBOARDING);
  }, []);

  const selectDropDownFields = [
    {
      type: 'img',
      data: 'iso3166alpha2',
      class: 'col-1 text-left position-relative',
      img: {
        sourcePath: './images/flags/1x1/',
        ext: '.svg',
        class: 'rounded-circle position-absolute top-50 start-0 translate-middle-y',
      },
    },
    {
      type: 'text',
      data: 'displayName',
      class: 'col-11 ps-2 text-left',
    },
  ];

  const { data: standards } = useQuery(SETTINGS_TIER_STANDARD_ALL_KEY);

  useEffect(() => {
    if (!state?.email && !state?.partialSignupData?.email) {
      history.push('/onboarding/signup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addNationality = () => {
    setAdditionalNationalities((prev) => prev + 1);
  };

  const removeNationality = (number) => {
    setAdditionalNationalities((prev) => Math.max(0, prev - 1));
    if (number === 'second' && form.third_selected_country_of_nationality_id) {
      setForm({
        ...form,
        [`${number}_selected_country_of_nationality_id`]:
          form.third_selected_country_of_nationality_id,
        [`${number}_selected_country_of_nationality_label`]:
          form.third_selected_country_of_nationality_label,
        [`${number}_selected_country_of_nationality_iso3166alpha2`]:
          form.third_selected_country_of_nationality_iso3166alpha2,
        [`${number}_selected_country_of_nationality_iso3166alpha3`]:
          form.third_selected_country_of_nationality_iso3166alpha3,
        [`third_selected_country_of_nationality_id`]: '',
        [`third_selected_country_of_nationality_label`]: '',
        [`third_selected_country_of_nationality_iso3166alpha2`]: '',
        [`third_selected_country_of_nationality_iso3166alpha3`]: '',
      });
    } else {
      setForm({
        ...form,
        [`${number}_selected_country_of_nationality_id`]: '',
        [`${number}_selected_country_of_nationality_label`]: '',
        [`${number}_selected_country_of_nationality_iso3166alpha2`]: '',
        [`${number}_selected_country_of_nationality_iso3166alpha3`]: '',
      });
    }
  };

  const { mutate: partialSignup, isLoading } = useMutation(PARTIAL_SIGN_UP, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        sendStats(MXP_ONBOARDING_SIGN_UP_PERSONAL_INFO_CONFIRM, {}, FLOWS.ONBOARDING);
        if (shouldAskAdditionalDetails) {
          history.push(`additional-details`, {
            ...form,
            ...state,
            additionalNationalities,
            purposeOfAccount: form.purposeOfAccount.key,
            partialSignupData: data,
          });
        } else {
          history.push(`address-information`, {
            ...form,
            ...state,
            purposeOfAccount: form.purposeOfAccount.key,
            partialSignupData: data,
          });
        }
      }
    },
  });

  const purposeOfAccountOptions = useMemo(() => {
    if (standards) {
      return standards?.filter((item) => item.risk_type_key === 'purpose_of_account');
    }
    return [];
  }, [standards]);

  const MAX_DATE = moment().subtract(18, 'year').toDate();
  const MIN_DATE = moment('01/01/1900').toDate();

  const getSSNMasked = (value) => {
    const valueLength = value.length;
    let maskedSSN = value;

    if (form.ssn && valueLength < form.ssn.length) {
      return value;
    }

    if (valueLength >= 3 && maskedSSN[3] !== '-') {
      maskedSSN = `${maskedSSN.slice(0, 3)}-${maskedSSN.slice(3)}`;
    }

    if (valueLength >= 6 && maskedSSN[6] !== '-') {
      maskedSSN = `${maskedSSN.slice(0, 6)}-${maskedSSN.slice(6)}`;
    }

    return maskedSSN;
  };

  const handleChange = (e, fieldName) => {
    if (!e) return;

    if (!e?.target) {
      if (
        [
          'selected_country_of_nationality',
          'second_selected_country_of_nationality',
          'third_selected_country_of_nationality',
          'place_of_birth',
        ].includes(fieldName)
      ) {
        setForm({
          ...form,
          [`${fieldName}_id`]: e.id,
          [`${fieldName}_label`]: e.displayName,
          [`${fieldName}_iso3166alpha2`]: e.iso3166alpha2,
          [`${fieldName}_iso3166alpha3`]: e.iso3166alpha3,
        });
      } else {
        setForm({ ...form, [fieldName]: e });
      }
    } else if (
      [
        'selected_country_of_nationality',
        'second_selected_country_of_nationality',
        'third_selected_country_of_nationality',
        'place_of_birth',
      ].includes(fieldName)
    ) {
      setForm({
        ...form,
        [`${fieldName}_id`]: e.id,
        [`${fieldName}_label`]: e.displayName,
        [`${fieldName}_iso3166alpha2`]: e.iso3166alpha2,
        [`${fieldName}_iso3166alpha3`]: e.iso3166alpha3,
      });
    } else {
      const { name, value } = e?.target || {};
      if (name === 'ssn') {
        setForm({ ...form, [name]: getSSNMasked(value) });
      } else if (name === 'dateOfBirth') {
        const dateValue = moment(value, isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY');
        const isValidDate = dateValue.isValid();
        const validDateString = isValidDate
          ? `${dateValue.year()}-${dateValue.format('MM')}-${dateValue.format('DD')}T00:00:00`
          : '';
        setForm({ ...form, [name]: isValidDate ? new Date(validDateString) : null });
      } else {
        setForm({ ...form, [name]: value });
      }
    }
  };

  const handleBlur = (e) => {
    handleFormBlur(e);
    if (!e || !e.target) return;
    if (e.target.name === 'ssn' && e.target.value) {
      if (!validateSSN(e.target.value)) {
        if (e.target.className.indexOf('has-error') === -1) {
          e.target.className += ' has-error';
        }
      }
    }

    if (e.target.name === 'dateOfBirth' && e.target.value) {
      const isValidDate = moment(e.target.value).isValid();
      const validDateString = isValidDate
        ? `${moment(e.target.value).year()}-${moment(e.target.value).format('MM')}-${moment(
            e.target.value
          ).format('DD')}T00:00:00`
        : '';
      const date = new Date(validDateString);
      if (isValidDate && date > MAX_DATE && date < MIN_DATE) {
        if (e.target.className.indexOf('has-error') === -1) {
          e.target.className += ' has-error';
        }
      }
    }

    if (['middle_name', 'first_name', 'last_name'].includes(e.target.name) && e.target.value) {
      if (
        !validateLatinLettersOnly(e.target.value) &&
        e.target.className.indexOf('has-error') === -1
      ) {
        e.target.className += ' has-error';
      }
    }

    e.target.closest('.form-group').className = e.target
      .closest('.form-group')
      .className.replace('has-focus', '');
  };

  const clearError = (e) => {
    e.target.className = e.target.className.replace('has-error', '');
    if (e.target.closest('.form-group').className.indexOf('has-focus') === -1) {
      e.target.closest('.form-group').className = `${
        e.target.closest('.form-group').className
      } has-focus`;
    }
  };

  const handleDateOfBirthFocus = (e) => {
    clearError(e);
    setDisplayBirtDateLabel(true);
  };

  const handleDateOfBirthBlur = (e) => {
    handleBlur(e);
    setDisplayBirtDateLabel(false);
  };

  const convertLocalToUTCDate = (_date) => {
    let date = _date;
    if (!date) {
      return date;
    }

    date = new Date(date);
    date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

    return date;
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();

    if (allowPartialOnboarding) {
      const personalInfoPhaseObj = {
        id: state?.partialSignupData?.id,
        phase: PARTIAL_SIGN_UP_PHASES.PERSONAL_INFO,
        first_name: form?.first_name,
        last_name: form?.last_name,
        middle_name: form?.middle_name,
        date_of_birth: convertLocalToUTCDate(form.dateOfBirth).toISOString().split('T')[0],
        dateOfBirth: convertLocalToUTCDate(form.dateOfBirth).toISOString().split('T')[0],
        selected_country_of_nationality:
          form?.selected_country_of_nationality_iso3166alpha3 || state?.countryOfResidenceAlpha,
        purpose_of_account: form?.purposeOfAccount?.key,
        title: 'Other',
        gender: 'Other',
        ssn: form?.ssn,
        verification_id: state?.verification_id,
      };
      if (isCountryOfBirthAsked && form.place_of_birth_iso3166alpha3) {
        personalInfoPhaseObj.country_of_birth = form.place_of_birth_iso3166alpha3;
      }
      if (form?.second_selected_country_of_nationality_iso3166alpha3) {
        personalInfoPhaseObj.second_selected_country_of_nationality =
          form.second_selected_country_of_nationality_iso3166alpha3;
      }
      if (form?.third_selected_country_of_nationality_iso3166alpha3) {
        personalInfoPhaseObj.third_selected_country_of_nationality =
          form.third_selected_country_of_nationality_iso3166alpha3;
      }
      if (form.portugalInvestmentScheme) {
        personalInfoPhaseObj.portugal_investment_scheme = form.portugalInvestmentScheme;
      }

      partialSignup(personalInfoPhaseObj);
    } else {
      const historyState = {
        ...state,
        ...form,
        additionalNationalities,
        date_of_birth: convertLocalToUTCDate(form.dateOfBirth).toISOString().split('T')[0],
        dateOfBirth: convertLocalToUTCDate(form.dateOfBirth).toISOString().split('T')[0],
        purposeOfAccount: form.purposeOfAccount.key,
      };

      sendStats(MXP_ONBOARDING_SIGN_UP_PERSONAL_INFO_CONFIRM, {}, FLOWS.ONBOARDING);
      if (shouldAskAdditionalDetails) {
        history.push(`additional-details`, historyState);
      } else {
        history.push(`address-information`, historyState);
      }
    }
  };

  const disabledButton = useMemo(
    () =>
      !validateLatinLettersOnly(form.first_name) ||
      !validateLatinLettersOnly(form.last_name) ||
      !validateLatinLettersOnly(form.middle_name, true) ||
      (isUS && !validateSSN(form.ssn)) ||
      form.dateOfBirth === '' ||
      form.dateOfBirth > MAX_DATE ||
      form.dateOfBirth < MIN_DATE ||
      !moment(form.dateOfBirth).isValid() ||
      !form.purposeOfAccount.key ||
      isLoading ||
      !form.selected_country_of_nationality_label ||
      (isCountryOfBirthAsked && !form.place_of_birth_label),
    [form, isUS, MAX_DATE, MIN_DATE, isLoading, isCountryOfBirthAsked]
  );

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          loading={isLoading ? 'true' : 'false'}
          form="personal-information-form"
          disabled={disabledButton}>
          {translate('confirm_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          loading={isLoading ? 'true' : 'false'}
          form="personal-information-form"
          disabled={disabledButton}>
          {translate('confirm_button_label')}
        </button>
      </div>
    </>
  );

  return (
    <OnboardingBodyLayout
      title={translate('personal_information_title_label')}
      renderButtons={renderButtons}>
      <form
        onSubmit={handleSubmitForm}
        id="personal-information-form"
        className="w-100 personal-info-custom">
        <div className="d-flex w-100 flex-fill flex-column gap-0 flex-md-row gap-4 first-middle-name-container">
          <Input
            containerClass="w-100"
            groupClassName={`mb-0 mt-4 ${isIOS ? 'ios' : ''}`}
            placeholder={translate('first_name_input_label')}
            label={translate('first_name_input_label')}
            errorLabel={
              !form.first_name
                ? translate('input_required_error_label')
                : translate('valid_letter_error_label')
            }
            className={`${isIOS ? 'ios' : ''}`}
            name="first_name"
            value={form.first_name}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={clearError}
            withFloatingLabel
            withErrorLabel
          />
          <Input
            containerClass="w-100"
            className={`${isIOS ? 'ios' : ''}`}
            groupClassName={`mb-0 mt-4 ${isIOS ? 'ios' : ''}`}
            placeholder={translate('middle_name_input_label')}
            label={translate('middle_name_input_label')}
            errorLabel={translate('valid_letter_error_label')}
            name="middle_name"
            value={form.middle_name}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={clearError}
            withFloatingLabel
            withErrorLabel
          />
        </div>
        <div className="d-flex justify-content-between gap-md-4 flex-column flex-md-row">
          <Input
            containerClass="w-100"
            className={`${isIOS ? 'ios' : ''}`}
            groupClassName={`mb-0 ${isIOS ? 'ios' : ''}`}
            placeholder={translate('last_name_input_label')}
            label={translate('last_name_input_label')}
            errorLabel={
              !form.last_name
                ? translate('input_required_error_label')
                : translate('valid_letter_error_label')
            }
            name="last_name"
            value={form.last_name}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={clearError}
            withFloatingLabel
            withErrorLabel
            required
          />
          {isUS && (
            <Input
              containerClass="w-100"
              className={`${isIOS ? 'ios' : ''}`}
              groupClassName={`mb-0 ${isIOS ? 'ios' : ''}`}
              placeholder={translate('ssn_input_label')}
              label={translate('ssn_input_label')}
              errorLabel={translate('ssn_input_error_label')}
              name="ssn"
              value={form.ssn}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={clearError}
              withFloatingLabel
              withErrorLabel
              required
            />
          )}
        </div>
        <div className="form-group form-group-input-date dateOfBirth mt-md-5">
          <DatePicker
            open={false}
            placeholderText={
              isUS ? translate('date_us_input_label') : translate('date_input_label')
            }
            selected={form?.dateOfBirth ? moment(form?.dateOfBirth).toDate() : null}
            className={`form-control-input w-100 ${isIOS ? 'ios' : ''} ${
              form.dateOfBirth ? 'text-dark' : 'text-shadow'
            }`}
            name="dateOfBirth"
            id="dateOfBirth"
            onBlur={handleDateOfBirthBlur}
            customInput={
              <InputMask
                mask={isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}
                showMask
                replacement={{ D: /\d/, M: /\d/, Y: /\d/ }}
              />
            }
            onFocus={handleDateOfBirthFocus}
            onCalendarClose={() => {
              document.activeElement.blur();
            }}
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            onChangeRaw={(event) => {
              handleChange(event, 'dateOfBirth');
              handleDateOfBirthBlur();
            }}
            maxDate={MAX_DATE}
            dropdownMode="select"
            dateFormat={isUS ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
            required
          />
          {(displayBirthDateLabel || form.dateOfBirth) && (
            <label className="floating-label">
              {isUS ? translate('date_us_input_label') : translate('date_input_label')}
            </label>
          )}
        </div>
        <div
          className="form-group nationality position-relative d-flex flex-column gap-3"
          style={{ position: 'unset' }}>
          <SelectCountryDropdown
            name="selected_country_of_nationality"
            placeholder={translate('nationality_select_label')}
            getOptionLabel={(option) => option.displayName}
            isOptionSelected={(option) => option.id === form.selected_country_of_nationality_id}
            value={form.selected_country_of_nationality_label}
            defaultValue={form.selected_country_of_nationality_iso3166alpha3}
            flagValue={form.selected_country_of_nationality_iso3166alpha2}
            onChange={(data, name) => handleChange(data, name)}
            handleBlur={handleBlur}
            clearError={clearError}
            onFocus={() => {
              setIsPurposeOfAccountOpened(false);
            }}
            optionName="displayName"
            prefix=""
            fieldsInfo={selectDropDownFields}
            containerClass="w-100 position-absolute start-0 bg-light p-3 mt-2 rounded shadow-lg"
            dropDownParentClass=""
            withValueImageFlag
            useFilteredCountries
            showAllCountriesNoRestrictions
          />
          {isBOPPhaseOn && isCorEuOrUk && additionalNationalities === 0 && (
            <button
              onClick={() => {
                addNationality();
              }}
              type="button"
              className="btn text-primary rounded-3 text-f6 m-0 p-0 text-nowrap text-btn-item border-0 d-flex align-items-center gap-2">
              <div style={{ width: '1.5rem', height: '1.5rem' }}>
                <Icon icon={ICONS.CIRCLE_PLUS} color={MAIN_ICON_COLORS.PRIMARY} />
              </div>
              <b>{translate('add_second_nationality_btn_label')}</b>
            </button>
          )}
        </div>
        {Array(additionalNationalities)
          .fill(0)
          .map((item, index) => {
            const fieldName =
              index === 0
                ? 'second_selected_country_of_nationality'
                : 'third_selected_country_of_nationality';
            const id =
              index === 0
                ? form?.second_selected_country_of_nationality_id
                : form?.third_selected_country_of_nationality_id;
            const nationalityNumber = index === 0 ? 'second' : 'third';

            const filterCountryIds = [form.selected_country_of_nationality_id];
            if (form.second_selected_country_of_nationality_id) {
              filterCountryIds.push(form.second_selected_country_of_nationality_id);
            }
            if (form.third_selected_country_of_nationality_id) {
              filterCountryIds.push(form.third_selected_country_of_nationality_id);
            }

            return (
              // eslint-disable-next-line react/no-array-index-key
              <div
                key={`additional-nationality-${form[`${fieldName}_label`]}`}
                className="d-flex flex-column gap-4">
                <div className="d-flex gap-4 align-content-center">
                  <div
                    className="form-group mt-0 nationality position-relative d-flex flex-column flex-fill gap-3"
                    style={{ position: 'unset' }}>
                    <SelectCountryDropdown
                      name={fieldName}
                      placeholder={translate('nationality_select_label')}
                      getOptionLabel={(option) => option.displayName}
                      isOptionSelected={(option) => option.id === id}
                      value={form[`${fieldName}_label`] || ''}
                      flagValue={form[`${fieldName}_iso3166alpha2`] || ''}
                      onChange={(data, name) => handleChange(data, name)}
                      handleBlur={handleBlur}
                      clearError={clearError}
                      filterCountryIds={filterCountryIds}
                      onFocus={() => {
                        setIsPurposeOfAccountOpened(false);
                      }}
                      optionName="displayName"
                      prefix=""
                      fieldsInfo={selectDropDownFields}
                      containerClass="w-100 position-absolute start-0 bg-light p-3 mt-2 rounded shadow-lg"
                      dropDownParentClass=""
                      withValueImageFlag
                      useFilteredCountries
                    />
                    {additionalNationalities === 1 && (
                      <button
                        onClick={() => {
                          addNationality();
                        }}
                        type="button"
                        className="btn text-primary rounded-3 text-f6 m-0 p-0 text-nowrap text-btn-item border-0 d-flex align-items-center gap-2">
                        <div style={{ width: '1.5rem', height: '1.5rem' }}>
                          <Icon icon={ICONS.CIRCLE_PLUS} color={MAIN_ICON_COLORS.PRIMARY} />
                        </div>
                        <b>{translate('add_third_nationality_btn_label')}</b>
                      </button>
                    )}
                  </div>
                  <div className="pt-2">
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <button
                      onClick={() => {
                        removeNationality(nationalityNumber);
                      }}
                      type="button"
                      className="btn text-primary text-f6 m-0 p-0 text-nowrap text-btn-item border-0 gap-2">
                      <div style={{ width: '2rem', height: '2rem' }}>
                        <Icon icon={ICONS.CIRCLE_MINUS} color={MAIN_ICON_COLORS.DANGER} />
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        {isCountryOfBirthAsked && (
          <div
            className="form-group nationality mt-0 position-relative d-flex flex-column gap-3"
            style={{ position: 'unset' }}>
            <SelectCountryDropdown
              name="place_of_birth"
              placeholder={translate('place_of_birth_select_label')}
              getOptionLabel={(option) => option.displayName}
              isOptionSelected={(option) => option.id === form.place_of_birth_id}
              value={form.place_of_birth_label}
              defaultValue={form.place_of_birth_iso3166alpha3}
              flagValue={form.place_of_birth_iso3166alpha2}
              onChange={(data, name) => handleChange(data, name)}
              handleBlur={handleBlur}
              clearError={clearError}
              onFocus={() => {
                setIsPurposeOfAccountOpened(false);
              }}
              optionName="displayName"
              prefix=""
              fieldsInfo={selectDropDownFields}
              containerClass="w-100 position-absolute start-0 bg-light p-3 mt-2 rounded shadow-lg"
              dropDownParentClass=""
              withValueImageFlag
              useFilteredCountries
            />
          </div>
        )}
        <div className="form-group mb-0" style={{ marginTop: 0 }}>
          <CustomDropdown
            name="purposeOfAccount"
            placeholder={translate('purpose_of_account_label')}
            onChange={handleChange}
            handleBlur={() => {
              setIsPurposeOfAccountOpened(false);
              handleBlur();
            }}
            clearError={clearError}
            value={form.purposeOfAccount}
            options={purposeOfAccountOptions}
            isOpened={isPurposeOfAccountOpened}
            setIsOpened={setIsPurposeOfAccountOpened}
          />
        </div>
        {isCORPortugal && (
          <div className="form-check d-flex flex-row align-items-center mt-5">
            <div>
              <input
                type="checkbox"
                className="form-check-input"
                checked={form.portugalInvestmentScheme}
                onChange={(e) => {
                  setForm({ ...form, portugalInvestmentScheme: e.target.checked });
                }}
              />
            </div>
            <label className="form-check-label letter-spacing-sm">
              <p className="ps-2 text-f6">{translate('pt_investment_scheme_question_label')} </p>
            </label>
          </div>
        )}
      </form>
    </OnboardingBodyLayout>
  );
};

export default AddPersonalInformation;
